import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, LinearProgress, Paper, Snackbar, SnackbarCloseReason,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, alpha, useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { LinhasEsqueletoTabela } from '../../../shared/components';
import { FilterBar } from '../../../shared/components/ui';
import { BoletosReceberService, IListagemBoletoReceber, TStatusBoletoReceber } from '../../../shared/services/api/Financeiro/BoletosReceber';
import { formatarMoeda } from '../../../shared/utils/formatarMoeda';
import { DialogCompensarBoletos } from './DialogCompensarBoletos';
import { CelulaListaMobile, LinhaListagemMobile, ListaMobile, useCelular } from '../../../shared/components/listagem-mobile';

type TFiltroStatus = 'todos' | TStatusBoletoReceber | 'ATRASADO';

const CHIPS_STATUS: { value: TFiltroStatus; label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'A_COMPENSAR', label: 'Em Aberto' },
  { value: 'COMPENSADO', label: 'Compensado' },
  // Derivado: A_COMPENSAR cuja data prevista de compensação já passou sem
  // confirmação — não é um status gravado, ver getAllBoletosReceber (backend).
  { value: 'ATRASADO', label: 'Atrasados' },
];

// Mesmo critério do filtro "Atrasados": Em Aberto com a data prevista de
// compensação no passado — usado pra destacar a linha mesmo fora desse
// filtro (ex.: dentro de "Todos"/"Em Aberto").
const estaAtrasado = (row: IListagemBoletoReceber) =>
  row.status === 'A_COMPENSAR' && dayjs(row.datacompensacaoprevista).isBefore(dayjs(), 'day');

// Tela de "Recebíveis - Boletos": diferente do extrato genérico de caixa
// (usado pras demais contas na Conciliação), aqui cada linha é um TÍTULO de
// boleto rastreado (ver tabela `boletosreceber`, backend), com status
// próprio — Em Aberto até a compensação real ser confirmada, Compensado
// depois. Renderizada só quando a conta de recebíveis selecionada é a fixa
// de Boleto (ver Conciliacao.tsx).
export const ListagemBoletosRecebiveis: React.FC = () => {
  const listaCelular = useCelular();
  const theme = useTheme();
  const [rows, setRows] = useState<IListagemBoletoReceber[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState<TFiltroStatus>('A_COMPENSAR');
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [openCompensar, setOpenCompensar] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const buscar = () => {
    setIsLoading(true);
    setSelecionados(new Set());
    BoletosReceberService.getAll(filtroStatus).then((resultado) => {
      setIsLoading(false);
      if (resultado instanceof Error) {
        setTms('error');
        setMsg(resultado.message);
        setOpenmsg(true);
        return;
      }
      setRows(resultado.data);
    });
  };

  useEffect(() => {
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroStatus]);

  const handleCloseMsg = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
  };

  const toggleSelecionado = (idboletoreceber: string) => {
    setSelecionados((atual) => {
      const novo = new Set(atual);
      if (novo.has(idboletoreceber)) novo.delete(idboletoreceber); else novo.add(idboletoreceber);
      return novo;
    });
  };

  const linhasSelecionaveis = rows.filter((r) => r.status === 'A_COMPENSAR');
  const todasSelecionadas = linhasSelecionaveis.length > 0 && linhasSelecionaveis.every((r) => selecionados.has(r.idboletoreceber));
  const toggleSelecionarTodas = () => {
    setSelecionados(todasSelecionadas ? new Set() : new Set(linhasSelecionaveis.map((r) => r.idboletoreceber)));
  };

  const boletosSelecionados = rows.filter((r) => selecionados.has(r.idboletoreceber));
  const totalSelecionado = boletosSelecionados.reduce((soma, b) => soma + Number(b.valorbruto || 0), 0);

  const handleCloseCompensar = (newValue?: string) => {
    setOpenCompensar(false);
    if (newValue === 'true') {
      setTms('success');
      setMsg('Boletos compensados com sucesso!');
      setOpenmsg(true);
      buscar();
    }
  };

  return (
    <Box sx={{ mx: 1 }}>
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 2500} onClose={handleCloseMsg}>
        <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: '100%' }}>
          {msg}
        </Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 1.5 }}>
        <FilterBar chips={CHIPS_STATUS} value={filtroStatus} onChange={setFiltroStatus} />
        <Button
          variant="contained"
          size="small"
          disabled={boletosSelecionados.length === 0}
          onClick={() => setOpenCompensar(true)}
        >
          Compensar selecionados{boletosSelecionados.length > 0 ? ` (${formatarMoeda(totalSelecionado)})` : ''}
        </Button>
      </Box>

      {isLoading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 'calc(100vh - 420px)', overflow: 'auto' }}>
        <Table stickyHeader size="small">
          {!listaCelular && (
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ backgroundColor: theme.palette.background.paper }}>
                <Checkbox
                  size="small"
                  checked={todasSelecionadas}
                  indeterminate={!todasSelecionadas && selecionados.size > 0}
                  disabled={linhasSelecionaveis.length === 0}
                  onChange={toggleSelecionarTodas}
                />
              </TableCell>
              <TableCell sx={{ backgroundColor: theme.palette.background.paper, fontWeight: 700 }}>Parceiro</TableCell>
              <TableCell sx={{ backgroundColor: theme.palette.background.paper, fontWeight: 700 }}>CR</TableCell>
              <TableCell align="center" sx={{ backgroundColor: theme.palette.background.paper, fontWeight: 700 }}>Pagamento</TableCell>
              <TableCell align="center" sx={{ backgroundColor: theme.palette.background.paper, fontWeight: 700 }}>Previsão Comp.</TableCell>
              <TableCell align="center" sx={{ backgroundColor: theme.palette.background.paper, fontWeight: 700 }}>Compensado em</TableCell>
              <TableCell align="right" sx={{ backgroundColor: theme.palette.background.paper, fontWeight: 700 }}>Valor Bruto</TableCell>
              <TableCell align="right" sx={{ backgroundColor: theme.palette.background.paper, fontWeight: 700 }}>Tarifa</TableCell>
              <TableCell align="right" sx={{ backgroundColor: theme.palette.background.paper, fontWeight: 700 }}>Valor Líquido</TableCell>
              <TableCell align="center" sx={{ backgroundColor: theme.palette.background.paper, fontWeight: 700 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          )}
          <TableBody>
            {isLoading && rows.length === 0 ? (
              <LinhasEsqueletoTabela
                colunas={[
                  { variant: 'rounded', width: 18, height: 18, align: 'center' },
                  { width: '75%' },
                  { width: '55%' },
                  { width: '55%', align: 'center' },
                  { width: '55%', align: 'center' },
                  { width: '55%', align: 'center' },
                  { width: '55%', align: 'right' },
                  { width: '45%', align: 'right' },
                  { width: '55%', align: 'right' },
                  { variant: 'rounded', width: 60, height: 20, align: 'center' },
                ]}
              />
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} sx={{ py: 0 }}>
                  <EmptyBoletos />
                </TableCell>
              </TableRow>
            ) : listaCelular ? (
              <CelulaListaMobile>
                <ListaMobile
                  linhas={rows}
                  chave={(row) => row.idboletoreceber}
                  renderizar={(row) => (
                    <LinhaListagemMobile
                      avatar={row.parceironome || '?'}
                      titulo={row.parceironome}
                      detalhes={[row.numerocr, `Pago ${dayjs(row.datapagamento).format('DD/MM/YY')}`, `Compensa ${dayjs(row.datacompensacaoprevista).format('DD/MM/YY')}`]}
                      valor={formatarMoeda(Number(row.valorliquido))}
                      corValor="success.main"
                      etiqueta={estaAtrasado(row)
                        ? { rotulo: 'Atrasado', cor: 'error' }
                        : row.status === 'A_COMPENSAR' ? { rotulo: 'Em Aberto', cor: 'warning' } : row.status === 'COMPENSADO' ? { rotulo: 'Compensado', cor: 'success' } : { rotulo: 'Estornado' }}
                      destaque={selecionados.has(row.idboletoreceber)}
                      onClick={row.status === 'A_COMPENSAR' ? () => toggleSelecionado(row.idboletoreceber) : undefined}
                      acoes={(
                        <Checkbox size="small" checked={selecionados.has(row.idboletoreceber)} disabled={row.status !== 'A_COMPENSAR'}
                          onChange={() => toggleSelecionado(row.idboletoreceber)} />
                      )}
                    />
                  )}
                />
              </CelulaListaMobile>
            ) : rows.map((row) => {
              const atrasado = estaAtrasado(row);
              return (
              <TableRow key={row.idboletoreceber} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selecionados.has(row.idboletoreceber)}
                    disabled={row.status !== 'A_COMPENSAR'}
                    onChange={() => toggleSelecionado(row.idboletoreceber)}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: '0.84rem' }}>{row.parceironome}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.84rem' }}>{row.numerocr}</TableCell>
                <TableCell align="center" sx={{ fontSize: '0.84rem' }}>{dayjs(row.datapagamento).format('DD/MM/YY')}</TableCell>
                <TableCell align="center" sx={{ fontSize: '0.84rem', color: atrasado ? 'error.main' : undefined, fontWeight: atrasado ? 600 : undefined }}>
                  {dayjs(row.datacompensacaoprevista).format('DD/MM/YY')}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.84rem' }}>
                  {row.datacompensacaoefetiva ? dayjs(row.datacompensacaoefetiva).format('DD/MM/YY') : '—'}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.84rem' }}>{formatarMoeda(Number(row.valorbruto))}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.84rem', color: Number(row.tarifa) > 0 ? 'error.main' : 'text.secondary' }}>
                  {formatarMoeda(Number(row.tarifa))}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.84rem', fontWeight: 600 }}>{formatarMoeda(Number(row.valorliquido))}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={atrasado ? 'Atrasado' : row.status === 'A_COMPENSAR' ? 'Em Aberto' : row.status === 'COMPENSADO' ? 'Compensado' : 'Estornado'}
                    size="small"
                    sx={{
                      height: '20px', fontSize: '0.68rem', fontWeight: 600,
                      backgroundColor: atrasado
                        ? alpha(theme.palette.error.main, 0.14)
                        : row.status === 'A_COMPENSAR' ? alpha(theme.palette.warning.main, 0.14) : alpha(theme.palette.success.main, 0.14),
                      color: atrasado
                        ? theme.palette.error.dark
                        : row.status === 'A_COMPENSAR' ? theme.palette.warning.dark : theme.palette.success.dark,
                    }}
                  />
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <DialogCompensarBoletos
        open={openCompensar}
        boletos={boletosSelecionados}
        onClose={handleCloseCompensar}
      />
    </Box>
  );
};

const EmptyBoletos: React.FC = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 6 }}>
    <ReceiptLongIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
    <Typography variant="h6" color="text.secondary">Nenhum boleto encontrado</Typography>
  </Box>
);
